import { useEffect, useState } from "react";
import { Check, RefreshCw, MessageCircle, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money2, dateShort, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field } from "@/components/Bits";

const REASONS = ["Price", "Color", "Size", "Style", "Material", "Other"];
const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-lime";

function Modal({ title, children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-6" data-testid={testId}>
      <div className="absolute inset-0 bg-ink/35 backdrop-blur-sm" onClick={onClose} />
      <div className="card-surface relative max-h-[92vh] w-full max-w-lg overflow-auto p-7 animate-rise">
        <div className="mb-5 flex items-start justify-between gap-4">
          <h3 className="font-serif text-2xl leading-snug text-ink">{title}</h3>
          <button onClick={onClose} className="rounded-sm p-1 text-stone hover:text-ink" aria-label="Close">
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function ApprovalActions({ product, onChanged, compact }) {
  const { user, isStaff } = useAuth();
  const [open, setOpen] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [comment, setComment] = useState("");
  const [question, setQuestion] = useState("");
  const [thread, setThread] = useState([]);
  const [busy, setBusy] = useState(false);
  const [impact, setImpact] = useState(null);

  const vis = product?.visibility || {};
  const canApprove = product?.selection_status === "Awaiting Approval" && (isStaff || vis.allow_approval !== false);
  const canComment = isStaff || vis.allow_comments !== false;

  const loadThread = async () => {
    try {
      const { data } = await api.get(`/products/${product.id}/questions`);
      setThread(data);
    } catch {
      setThread([]);
    }
  };

  useEffect(() => {
    if (product?.id && canComment) loadThread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  useEffect(() => {
    if (open !== "approve" || !product?.project_id) return;
    setImpact(null);
    api
      .get(`/products/${product.id}/budget-impact`)
      .then((r) => setImpact(r.data))
      .catch(() => setImpact(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.id]);

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      setOpen(null);
      setReasons([]);
      setComment("");
      setQuestion("");
      onChanged?.();
      loadThread();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`approval-actions-${product.id}`}>
      {product.approved_at && (
        <div className="mb-4 rounded-sm border border-line bg-[#F4F5E4] px-4 py-3 text-xs text-ink" data-testid={`approved-stamp-${product.id}`}>
          Approved by {product.approved_by_name || "client"} · {dateShort(product.approved_at)}
          {product.changed_since_approval && (
            <span className="mt-1 block font-medium text-[#A8492E]">Changed since approval — re-approval may be needed</span>
          )}
        </div>
      )}
      {product.revision_request && (
        <div className="mb-4 rounded-sm border border-[#E07A5F] bg-[#F6DED6] px-4 py-3 text-xs text-[#A8492E]" data-testid={`revision-note-${product.id}`}>
          Alternative requested{product.revision_request.reasons?.length ? ` — ${product.revision_request.reasons.join(", ")}` : ""}
          {product.revision_request.comment && <span className="mt-1 block">“{product.revision_request.comment}”</span>}
        </div>
      )}

      <div className={`flex flex-wrap gap-3 ${compact ? "" : "mt-2"}`}>
        {canApprove && (
          <button
            data-testid={`approve-btn-${product.id}`}
            onClick={() => setOpen("approve")}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-sm bg-lime px-4 py-3 text-sm font-medium text-ink transition-colors hover:bg-lime-dark sm:flex-none"
          >
            <Check className="h-4 w-4" strokeWidth={1.5} /> Approve
          </button>
        )}
        {canApprove && (
          <button
            data-testid={`alternative-btn-${product.id}`}
            onClick={() => setOpen("alternative")}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-sm border border-line bg-white px-4 py-3 text-sm text-ink transition-colors hover:border-fog sm:flex-none"
          >
            <RefreshCw className="h-4 w-4" strokeWidth={1.5} /> Request Alternative
          </button>
        )}
        {canComment && (
          <button
            data-testid={`question-btn-${product.id}`}
            onClick={() => setOpen("question")}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-sm border border-line bg-white px-4 py-3 text-sm text-ink transition-colors hover:border-fog sm:flex-none"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> Ask a Question
            {thread.length > 0 && <span className="num text-xs text-fog">({thread.length})</span>}
          </button>
        )}
      </div>

      {open === "approve" && (
        <Modal title="Approve this selection?" onClose={() => setOpen(null)} testId="approve-modal">
          <div className="space-y-2">
            <div className="font-serif text-xl text-ink">{product.name}</div>
            {product.client_total != null && <div className="num text-lg text-ink">{money2(product.client_total)}</div>}
          </div>
          <p className="mt-5 text-sm leading-relaxed text-stone">
            By approving this selection, you are confirming this item for your project.
          </p>
          {impact && (impact.enabled || isStaff) && (
            <div className="mt-6 rounded-sm border border-line bg-[#F9F9F5] p-5" data-testid="budget-impact-panel">
              <div className="overline">If You Approve This</div>
              {!impact.enabled && isStaff && (
                <div className="mt-2 text-[11px] text-fog">Internal preview — budget impact is currently hidden from the client.</div>
              )}
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-stone">{impact.scope_label}</span>
                  <span className="num text-ink" data-testid="budget-impact-budget">{money2(impact.budget)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-stone">Already approved</span>
                  <span className="num text-ink" data-testid="budget-impact-approved">{money2(impact.already_approved)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-stone">This selection</span>
                  <span className="num text-ink" data-testid="budget-impact-selection">+{money2(impact.this_selection)}</span>
                </div>
                <div className="flex justify-between gap-4 border-t border-line pt-2">
                  <span className="text-ink">Remaining after approval</span>
                  <span
                    className={`num ${impact.within_budget ? "text-lime-dark" : "text-clay"}`}
                    data-testid="budget-impact-remaining"
                  >
                    {money2(impact.remaining_after)}
                  </span>
                </div>
              </div>
              <p className="mt-4 text-xs leading-relaxed text-stone" data-testid="budget-impact-message">
                {impact.within_budget
                  ? `This selection keeps the ${impact.scope === "space" ? "room" : "project"} within budget.`
                  : `This selection would place the ${impact.scope === "space" ? "room" : "project"} approximately ${money2(impact.over_by)} above its current budget.`}
              </p>
            </div>
          )}
          <div className="mt-7 flex gap-3">
            <button
              data-testid="confirm-approval-btn"
              disabled={busy}
              onClick={() => act(() => api.post(`/products/${product.id}/approve`), "Selection approved")}
              className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-60"
            >
              {busy ? "Approving…" : "Confirm Approval"}
            </button>
            <button onClick={() => setOpen(null)} className="rounded-sm border border-line px-5 py-2.5 text-sm text-ink hover:bg-line/50">Cancel</button>
          </div>
        </Modal>
      )}

      {open === "alternative" && (
        <Modal title="Request an Alternative" onClose={() => setOpen(null)} testId="alternative-modal">
          <p className="text-sm leading-relaxed text-stone">Tell us what isn’t working for you.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {REASONS.map((r) => (
              <button
                key={r}
                data-testid={`reason-${r.toLowerCase()}`}
                onClick={() => setReasons((s) => (s.includes(r) ? s.filter((x) => x !== r) : [...s, r]))}
                className={`rounded-sm border px-3 py-1.5 text-xs transition-colors ${
                  reasons.includes(r) ? "border-lime bg-lime text-ink" : "border-line bg-white text-stone hover:border-fog"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <Field label="Comment (optional)" className="mt-6">
            <textarea data-testid="alternative-comment-input" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} className={input} />
          </Field>
          <button
            data-testid="send-alternative-btn"
            disabled={busy}
            onClick={() => act(() => api.post(`/products/${product.id}/request-alternative`, { reasons, comment }), "Request sent to your designer")}
            className="mt-6 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send Request"}
          </button>
        </Modal>
      )}

      {open === "question" && (
        <Modal title={`Ask about ${product.name}`} onClose={() => setOpen(null)} testId="question-modal">
          <div className="space-y-4" data-testid="question-thread">
            {thread.map((q) => (
              <div key={q.id} className="rounded-sm border border-line p-4">
                <div className="text-sm text-ink">{q.body}</div>
                <div className="mt-1 text-[11px] text-fog">{q.author_name} · {timeAgo(q.created_at)}</div>
                {(q.replies || []).map((r) => (
                  <div key={r.id} className="mt-3 border-l-2 border-lime pl-3">
                    <div className="text-sm text-ink">{r.body}</div>
                    <div className="mt-1 text-[11px] text-fog">{r.author_name} · {timeAgo(r.created_at)}</div>
                  </div>
                ))}
                {isStaff && (
                  <ReplyBox questionId={q.id} onSent={loadThread} />
                )}
              </div>
            ))}
            {!thread.length && <p className="text-sm text-fog">No questions yet.</p>}
          </div>
          <Field label="Your question" className="mt-6">
            <textarea data-testid="question-input" rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} className={input} />
          </Field>
          <button
            data-testid="send-question-btn"
            disabled={busy || !question.trim()}
            onClick={() => act(() => api.post(`/products/${product.id}/questions`, { body: question }), "Question sent")}
            className="mt-5 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send Question"}
          </button>
        </Modal>
      )}
    </div>
  );
}

function ReplyBox({ questionId, onSent }) {
  const [text, setText] = useState("");
  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/questions/${questionId}/reply`, { body: text });
      setText("");
      toast.success("Reply sent");
      onSent?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <div className="mt-3 flex gap-2">
      <input
        data-testid={`reply-input-${questionId}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Reply to client…"
        className="flex-1 rounded-sm border border-line px-3 py-2 text-sm outline-none focus:border-lime"
      />
      <button data-testid={`reply-send-${questionId}`} onClick={send} className="rounded-sm bg-ink px-3 py-2 text-xs text-white hover:bg-stone">Reply</button>
    </div>
  );
}
